import { useEffect, useRef } from "react";
import { ArrowRightFromLine, Copy, Flag, GitBranch, History, LayoutGrid, Pencil, Play, Plus, Trash2, Zap } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { cn } from "@ncp/ui";
import { autoLayout, insertOnEdge, newEdge, newNode, NODE_W } from "../graph";
import { useStore } from "../store";
import { KIND_META } from "./nodes";
import type { Graph, NodeKind } from "../types";

export interface MenuState { kind: "node" | "edge" | "pane" | "insert"; id: string; x: number; y: number; flow?: { x: number; y: number } }

const INSERTABLE: NodeKind[] = ["Task", "Decision", "Agent", "Automation", "Wait", "SubProcess", "End"];

/** Right-click menu of the canvas (and the "+" on a transition). */
export function CanvasMenu({ menu, graph, editable, onClose, onOpenHistory, onFocusInspector }: {
  menu: MenuState; graph: Graph; editable: boolean; onClose: () => void; onOpenHistory?: (nodeId: string) => void; onFocusInspector?: (tab: string) => void;
}) {
  const { t } = useTranslation();
  const store = useStore();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) onClose(); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", esc);
    return () => { window.removeEventListener("mousedown", close); window.removeEventListener("keydown", esc); };
  }, [onClose]);

  const node = menu.kind === "node" ? graph.nodes.find((n) => n.id === menu.id) : undefined;
  const edge = menu.kind === "edge" || menu.kind === "insert" ? graph.edges.find((e) => e.id === menu.id) : undefined;
  const run = (fn: () => void) => { fn(); onClose(); };
  const kindLabel = (k: NodeKind) => t(`admin:builder.kinds.${k}`, { defaultValue: KIND_META[k].label });

  const addAfter = (kind: NodeKind) => run(() => store.update("Add step", (g) => {
    const from = g.nodes.find((n) => n.id === node!.id)!;
    const created = newNode(g, kind, { x: (from.position?.x ?? 0) + NODE_W + 80, y: from.position?.y ?? 0 });
    const out = g.edges.find((e) => e.from === from.key && e.to !== from.key);
    g.nodes.push(created);
    if (out) insertOnEdge(g, out.id!, g.nodes.pop()!);
    else g.edges.push(newEdge(g, from, created));
    queueMicrotask(() => store.select({ nodes: [created.id!], edges: [] }));
  }));

  const insert = (kind: NodeKind) => run(() => store.update("Insert step", (g) => {
    const e = g.edges.find((x) => x.id === edge!.id)!;
    const a = g.nodes.find((n) => n.key === e.from)?.position ?? { x: 0, y: 0 };
    const b = g.nodes.find((n) => n.key === e.to)?.position ?? { x: a.x + 400, y: a.y };
    const created = newNode(g, kind, { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 + 110 });
    insertOnEdge(g, e.id!, created);
    queueMicrotask(() => store.select({ nodes: [created.id!], edges: [] }));
  }));

  const addHere = (kind: NodeKind) => run(() => store.update("Add step", (g) => {
    const created = newNode(g, kind, menu.flow ?? { x: 0, y: 0 });
    if (kind === "Start" && !g.nodes.some((n) => n.kind === "Start")) g.initialState = created.key;
    g.nodes.push(created);
    queueMicrotask(() => store.select({ nodes: [created.id!], edges: [] }));
  }));

  return (
    <div ref={ref} className="absolute z-50 min-w-52 overflow-hidden rounded-xl border border-sand-200 bg-white/95 p-1 text-sm shadow-sand-lg backdrop-blur" style={{ left: menu.x, top: menu.y }} onContextMenu={(e) => e.preventDefault()}>
      {node ? (
        <>
          <Header>{node.label}</Header>
          <Item icon={Pencil} onClick={() => run(() => { store.select({ nodes: [node.id!], edges: [] }); onFocusInspector?.("general"); })}>{t("admin:builder.menu.edit")}</Item>
          {editable ? <>
            <Sub label={t("admin:builder.menu.addAfter")} icon={ArrowRightFromLine}>{INSERTABLE.map((k) => <KindItem key={k} kind={k} label={kindLabel(k)} onClick={() => addAfter(k)} />)}</Sub>
            <Item icon={Copy} onClick={() => run(() => store.update("Duplicate", (g) => {
              const src = g.nodes.find((n) => n.id === node.id)!;
              const copy = newNode(g, src.kind, { x: (src.position?.x ?? 0) + 40, y: (src.position?.y ?? 0) + 60 }, `${src.label} (copy)`);
              g.nodes.push({ ...structuredClone(src), id: copy.id, key: copy.key, label: copy.label, position: copy.position, triggers: null });
            }))}>{t("admin:builder.menu.duplicate")}</Item>
            {node.key !== graph.initialState ? <Item icon={Play} onClick={() => run(() => store.update("Set start", (g) => { g.initialState = node.key; }))}>{t("admin:builder.menu.setStart")}</Item> : null}
            {node.kind !== "End" ? <Item icon={Flag} onClick={() => run(() => store.update("Make end", (g) => { const n = g.nodes.find((x) => x.id === node.id); if (n) { n.kind = "End"; n.status = "Closed"; } }))}>{t("admin:builder.menu.makeEnd")}</Item> : null}
          </> : null}
          {onOpenHistory ? <Item icon={History} onClick={() => run(() => onOpenHistory(node.id!))}>{t("admin:builder.menu.history")}</Item> : null}
          {editable && node.key !== graph.initialState ? <Item icon={Trash2} danger onClick={() => run(() => store.update("Delete", (g) => { g.nodes = g.nodes.filter((n) => n.id !== node.id); g.edges = g.edges.filter((e) => e.from !== node.key && e.to !== node.key); }))}>{t("actions.delete")}</Item> : null}
        </>
      ) : null}
      {menu.kind === "edge" && edge ? (
        <>
          <Header>{edge.label ?? edge.trigger}</Header>
          <Item icon={Pencil} onClick={() => run(() => { store.select({ nodes: [], edges: [edge.id!] }); onFocusInspector?.("general"); })}>{t("admin:builder.menu.edit")}</Item>
          {editable ? <>
            <Sub label={t("admin:builder.menu.insertStep")} icon={Plus}>{INSERTABLE.filter((k) => k !== "End").map((k) => <KindItem key={k} kind={k} label={kindLabel(k)} onClick={() => insert(k)} />)}</Sub>
            <Item icon={Zap} onClick={() => run(() => store.update("Toggle automatic", (g) => { const e = g.edges.find((x) => x.id === edge.id); if (e) e.automatic = !e.automatic; }))}>{edge.automatic ? t("admin:builder.menu.makeManual") : t("admin:builder.menu.makeAutomatic")}</Item>
            <Item icon={Trash2} danger onClick={() => run(() => store.update("Delete transition", (g) => { g.edges = g.edges.filter((e) => e.id !== edge.id); }))}>{t("actions.delete")}</Item>
          </> : null}
        </>
      ) : null}
      {menu.kind === "insert" && edge ? (
        <>
          <Header>{t("admin:builder.menu.insertOn", { name: edge.label ?? edge.trigger })}</Header>
          {INSERTABLE.filter((k) => k !== "End").map((k) => <KindItem key={k} kind={k} label={kindLabel(k)} onClick={() => insert(k)} />)}
        </>
      ) : null}
      {menu.kind === "pane" ? (
        <>
          <Header>{t("admin:builder.menu.canvas")}</Header>
          {editable ? <Sub label={t("admin:builder.menu.addHere")} icon={Plus}>{(["Start", ...INSERTABLE] as NodeKind[]).map((k) => <KindItem key={k} kind={k} label={kindLabel(k)} onClick={() => addHere(k)} />)}</Sub> : null}
          {editable ? <>
            <Item icon={LayoutGrid} onClick={() => run(() => store.replace(autoLayout(store.getState().graph, "LR"), null, "Auto-layout"))}>{t("admin:builder.menu.layoutLR")}</Item>
            <Item icon={GitBranch} onClick={() => run(() => store.replace(autoLayout(store.getState().graph, "lanes"), null, "Auto-layout"))}>{t("admin:builder.menu.layoutLanes")}</Item>
          </> : null}
          <Item icon={Copy} onClick={() => run(() => store.select({ nodes: graph.nodes.map((n) => n.id!), edges: [] }))}>{t("admin:builder.menu.selectAll")}</Item>
        </>
      ) : null}
    </div>
  );
}

function Header({ children }: { children: React.ReactNode }) {
  return <p className="truncate px-2.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wider text-sand-500">{children}</p>;
}

function Item({ icon: Icon, children, onClick, danger }: { icon: typeof Plus; children: React.ReactNode; onClick: () => void; danger?: boolean }) {
  return (
    <button type="button" onClick={onClick} className={cn("flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-start transition hover:bg-sand-100", danger && "text-danger-700 hover:bg-danger-50")}>
      <Icon className="size-4 shrink-0 opacity-70" />{children}
    </button>
  );
}

function KindItem({ kind, label, onClick }: { kind: NodeKind; label: string; onClick: () => void }) {
  const meta = KIND_META[kind];
  return (
    <button type="button" onClick={onClick} className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-start transition hover:bg-aegold-50">
      <span className={cn("grid size-5 place-items-center rounded-md", meta.chip)}><meta.icon className="size-3" /></span>{label}
    </button>
  );
}

function Sub({ label, icon: Icon, children }: { label: string; icon: typeof Plus; children: React.ReactNode }) {
  return (
    <details className="group">
      <summary className="flex cursor-pointer list-none items-center gap-2 rounded-lg px-2.5 py-1.5 transition hover:bg-sand-100"><Icon className="size-4 opacity-70" />{label}<span className="ms-auto text-sand-400 group-open:rotate-90">›</span></summary>
      <div className="ms-3 border-s border-sand-200 ps-1">{children}</div>
    </details>
  );
}
